import { useClickAway, useKey } from "react-use";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState } from "react";

import { Badge } from "reactstrap";
import PropTypes from "prop-types";
import { fetchResultMapFeatures } from "../../actions/resultMap";
import { format } from "date-fns";
import { selectDatesWithResults } from "../../selectors/resultMap";

/* The current trial date as a chip (date + its rank among the flights), opening
 * the list of every flight to load another one. Used by the World Map bottom
 * bar and the breeder analytics page: each places it and its panel with its
 * own classes (`className` on the anchor, `popoverClassName` on the panel).
 * `compact` drops the chip around the date and the rank, for a trigger that
 * already sits in its own card. `greyDatesWithoutResults` is for the analytics
 * pages only: elsewhere a date without traits may still have a field map or a
 * plot map to show.
 */
export const TrialDateSelector = ({
  className,
  popoverClassName,
  compact = false,
  greyDatesWithoutResults = false,
}) => {
  const dispatch = useDispatch();
  const [trial, selectedDate, loadingResults] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.trial_date,
    resultMap.refreshingFeaturesByDate,
  ]);
  // Loaded by the analytics pages. While they load, every date waits, so none
  // is picked before it is known to be empty.
  const datesWithResults = useSelector(selectDatesWithResults);
  const unavailable = (date) =>
    !greyDatesWithoutResults
      ? null
      : loadingResults
      ? "Looking for results..."
      : datesWithResults && !datesWithResults.has(date)
      ? "No results available on this date"
      : null;

  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  useClickAway(popoverRef, () => setIsOpen(false));
  useKey("Escape", () => setIsOpen(false));

  if (!trial?.trial_dates?.length || !selectedDate) return null;

  return (
    <span className={className}>
      {isOpen && (
        <div className={popoverClassName} ref={popoverRef}>
          <div className="powerdash-component d-flex flex-column gap-1 overflow-auto">
            {trial.trial_dates.map((date) => {
              const reason = unavailable(date);

              return (
                <Badge
                  className={`hiphen-badge elevated ${
                    reason ? "disabled" : "clickable"
                  } ${date === selectedDate ? "active" : ""}`}
                  key={date}
                  data-tooltip-id={reason ? "tooltip" : undefined}
                  data-tooltip-content={reason ?? undefined}
                  onClick={() => {
                    if (reason) return;
                    setIsOpen(false);
                    dispatch(fetchResultMapFeatures(trial, date, false));
                  }}
                >
                  {format(date, "d MMMM yyyy")}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
      {compact ? (
        <span
          className="d-flex align-items-center gap-2 clickable"
          onClick={() => setIsOpen(true)}
        >
          <i className="fa-solid fa-calendar-days discrete-icon" />
          <Badge className="hiphen-badge active text-ellipsis">
            {format(selectedDate, "EE. d MMM yyyy")}
          </Badge>
        </span>
      ) : (
        <Badge
          className="hiphen-badge elevated ps-2 small d-flex align-items-center gap-2 clickable"
          onClick={() => setIsOpen(true)}
        >
          <i className="fa-solid fa-calendar-days discrete-icon" />
          <Badge className="hiphen-badge active text-ellipsis">
            {format(selectedDate, "EE. d MMM yyyy")}
          </Badge>
          <span className="flex-shrink-0">
            {trial.trial_dates.indexOf(selectedDate) + 1} /{" "}
            {trial.trial_dates.length}
          </span>
        </Badge>
      )}
    </span>
  );
};

TrialDateSelector.propTypes = {
  className: PropTypes.string,
  popoverClassName: PropTypes.string,
  compact: PropTypes.bool,
  greyDatesWithoutResults: PropTypes.bool,
};
